import React, { useReducer, useContext } from 'react';
import {
  deleteAuthentication, setAuthentication, setPermissions, decodeTokenPermissions,
} from './core';

/**
 * @typedef {object} ParazolStore
 * @property {boolean} isAuthenticated
 * @property {{[key:string]: Array<string>}|null} prm
 */

/**
 * @typedef {'SET_AUTHENTICATION' |
 * 'DELETE_AUTHENTICATION' |
 * 'SET_PERMISSIONS'} ParazolContextTypes
 */

/**
 * @callback ParazolReducer
 * @param {ParazolStore} state
 * @param {object} action
 * @param {ParazolContextTypes} action.type
 * @param {any} action.payload
 * @returns {ParazolStore}
 */

/**
 * @callback ParazolContextDispatch
 * @param {object} action
 * @param {ParazolContextTypes} action.type
 * @param {any} [action.payload]
 * @returns {void}
 */

const ParazolStateContext = React.createContext(null);
const ParazolDispatchContext = React.createContext(null);

const localStorageToken = window.localStorage.getItem('auth');
const localStoragePermissions = window.localStorage.getItem('prm');

/**
 * Initial state for the Parazol context.
 * @type {ParazolStore}
 */
const initStore = {
  isAuthenticated: !!localStorageToken,
  prm: localStoragePermissions ? JSON.parse(atob(localStoragePermissions)) : null,
};

/**
 * Reducer for the Parazol context.
 * @type {ParazolReducer} reducer
 */
export default function parazolReducer(state, action) {
  switch (action.type) {
    case 'SET_AUTHENTICATION': {
      setAuthentication(action.payload);
      return { ...state, isAuthenticated: true };
    }

    case 'SET_PERMISSIONS': {
      const userprm = decodeTokenPermissions(action.payload);
      setPermissions(userprm);
      return { ...state, prm: userprm };
    }

    case 'DELETE_AUTHENTICATION': {
      deleteAuthentication();
      return { isAuthenticated: false, prm: null };
    }

    default: {
      throw new Error(`Parazol context: Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * Provider for the auth context from Parazol
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function ParazolProvider({ children }) {
  const [state, dispatch] = useReducer(parazolReducer, initStore);

  return (
    <ParazolStateContext.Provider value={state}>
      <ParazolDispatchContext.Provider value={dispatch}>
        {children}
      </ParazolDispatchContext.Provider>
    </ParazolStateContext.Provider>
  );
}

/**
 * Custom hook for accessing the global application context and dispatch function.
 * @returns {[ParazolStore, ParazolContextDispatch]}
 */
const useParazolAuthContext = () => [
  useContext(ParazolStateContext),
  useContext(ParazolDispatchContext)];

export {
  ParazolProvider,
  ParazolStateContext,
  ParazolDispatchContext,
  useParazolAuthContext,
};
